import { KpiMetric, KpiCategory } from "store/kpi-config/KpiConfigTypes";
import {
  MetricConfig,
  PieChartConfig,
} from "components/performance/PerformanceTypes";
import { PIE_CHART_COLORS, formatMetricValue } from "./PerformanceReportUtils";

export const isMetricGoalEnabled = (
  category: KpiCategory | undefined,
  metricId: string,
): boolean => {
  if (!category) return true;
  const metric = category.metrics.find((m) => m.id === metricId);
  return metric?.aggregationType !== "cumulative";
};

export const convertKpiMetricToMetricConfig = (
  metric: KpiMetric,
): MetricConfig => {
  return {
    id: metric.id,
    title: {
      id: `performance.${metric.id}`,
      defaultMessage: metric.title,
    },
    type: metric.metricType,
    polarity: metric.polarity,
  };
};

export const convertKpiMetricToPieChartConfig = (
  metric: KpiMetric,
): PieChartConfig => {
  return {
    id: metric.id,
    title: {
      id: `performance.${metric.id}`,
      defaultMessage: metric.title,
    },
    colors: PIE_CHART_COLORS,
  };
};

export const getMetricsByDisplayType = (
  category: KpiCategory | undefined,
  displayType: "basic" | "pie" | "tree" | "productList",
): KpiMetric[] => {
  if (!category) return [];
  return category.metrics.filter(
    (metric) => metric.displayType === displayType,
  );
};

export const getBasicMetricsConfig = (
  category: KpiCategory | undefined,
): MetricConfig[] => {
  const basicMetrics = getMetricsByDisplayType(category, "basic");
  return basicMetrics.map(convertKpiMetricToMetricConfig);
};

export const getPieChartMetricsConfig = (
  category: KpiCategory | undefined,
): PieChartConfig[] => {
  const pieMetrics = getMetricsByDisplayType(category, "pie");
  return pieMetrics.map(convertKpiMetricToPieChartConfig);
};

export const getProductListMetrics = (
  category: KpiCategory | undefined,
): KpiMetric[] => {
  return getMetricsByDisplayType(category, "productList");
};

export const getTreeMetrics = (
  category: KpiCategory | undefined,
): KpiMetric[] => {
  return getMetricsByDisplayType(category, "tree");
};

export const convertBasicMetricsToPayload = (
  metrics: KpiMetric[],
): { id: string; name: string }[] => {
  return metrics.map((metric) => ({
    id: metric.api.metricIds[0],
    name: metric.title,
  }));
};

export const getAllMetricIds = (metrics: KpiMetric[]): { id: string }[] => {
  const metricIds = new Set<string>();
  metrics.forEach((metric) => {
    metric.api.metricIds.forEach((id) => metricIds.add(id));
  });
  return Array.from(metricIds).map((id) => ({ id }));
};

export const findMetricById = (
  category: KpiCategory | undefined,
  metricId: string,
): KpiMetric | undefined => {
  if (!category) return undefined;
  return category.metrics.find((metric) => metric.id === metricId);
};

export const getProductListOptions = (
  category: KpiCategory | undefined,
): Array<{ value: string; label: string }> => {
  const productListMetrics = getProductListMetrics(category);
  return productListMetrics.map((metric) => ({
    value: metric.id,
    label: metric.title,
  }));
};

export const getKPIMetricConfigFromDynamic = (
  category: KpiCategory | undefined,
  selectedView: string,
) => {
  if (!category) {
    return {
      metric: "",
      metrics: [],
      sortMetric: "",
      sortDescending: true,
      sortBy: undefined,
      type: "number" as const,
      isMultiMetric: false,
      displayMetrics: [] as Array<{ metricId: string; label: string }>,
    };
  }

  const productListMetrics = getProductListMetrics(category);
  const selectedMetric = productListMetrics.find((m) => m.id === selectedView);

  if (!selectedMetric) {
    return {
      metric: "",
      metrics: [],
      sortMetric: "",
      sortDescending: true,
      sortBy: undefined,
      type: "number" as const,
      isMultiMetric: false,
      displayMetrics: [] as Array<{ metricId: string; label: string }>,
    };
  }

  const metricsArray = selectedMetric.api.metricIds;
  const displayMetrics = selectedMetric.api.displayMetrics || [];
  const isMultiMetric = displayMetrics.length > 1;
  const sortBy = selectedMetric.api.sortBy;

  return {
    metric: metricsArray[0],
    metrics: metricsArray,
    sortMetric: sortBy ? sortBy[0].metricId : metricsArray[0],
    sortDescending: sortBy ? sortBy[0].desc : true,
    sortBy: sortBy,
    type: selectedMetric.metricType,
    isMultiMetric,
    displayMetrics,
  };
};

export interface PerformanceRoute {
  name: { id: string; defaultMessage: string };
  link: string;
  disabled: boolean;
  activeIndex: number;
}

export const getTabIndexFromPath = (
  path: string,
  kpiCategories: Array<{ id: string; title: string }>,
  isKpiConfigLoaded: boolean,
): number | null => {
  if (isKpiConfigLoaded && kpiCategories.length > 0) {
    const categoryIndex = kpiCategories.findIndex((cat) =>
      path.includes(cat.id.toLowerCase()),
    );
    if (categoryIndex !== -1) return categoryIndex;
  }
  return null;
};

export const buildPerformanceRoutes = (
  kpiCategories: Array<{ id: string; title: string }>,
  isKpiConfigLoaded: boolean,
  baseUrl: string,
  disableSubNavTabs: boolean,
): PerformanceRoute[] => {
  const routes: PerformanceRoute[] = [];

  if (isKpiConfigLoaded && kpiCategories.length > 0) {
    kpiCategories.forEach((category, index) => {
      routes.push({
        name: {
          id: `performanceNavigationTabs.${category.id}`,
          defaultMessage: category.title,
        },
        link: `${baseUrl}/${category.id}`,
        disabled: disableSubNavTabs,
        activeIndex: index,
      });
    });
  }

  return routes;
};

export const getMultiMetricDisplay = (
  displayMetrics: Array<{ metricId: string; label: string }>,
  metricIds: string[],
  productMetricValues: number[],
): string => {
  if (
    !displayMetrics ||
    displayMetrics.length <= 1 ||
    !productMetricValues ||
    !metricIds
  ) {
    return "";
  }

  const metricLabels = displayMetrics.map((dm) => {
    const metricIndex = metricIds.indexOf(dm.metricId);
    const value = metricIndex !== -1 ? productMetricValues[metricIndex] : 0;
    return `${dm.label}: ${formatMetricValue(value, { type: "number" })}`;
  });

  const rateValue = formatMetricValue(productMetricValues[0], { type: "rate" });

  return `${metricLabels.join(", ")} (${rateValue})`;
};

export interface PieChartApiConfig {
  metricId: string;
  dimension: { id: string };
  metrics: { id: string }[];
  sortBy: { metricId: string; desc: boolean };
  limit: number;
}

export const getPieChartApiConfig = (
  metric: KpiMetric | undefined,
): PieChartApiConfig | null => {
  if (!metric || metric.displayType !== "pie" || !metric.api) {
    return null;
  }

  const { metricIds, dimensionIds, sortBy, limit } = metric.api;

  return {
    metricId: metric.id,
    dimension: { id: dimensionIds?.[0] || "" },
    metrics: metricIds.map((id) => ({ id })),
    sortBy: sortBy?.[0]
      ? { metricId: sortBy[0].metricId, desc: sortBy[0].desc }
      : { metricId: metricIds[0], desc: true },
    limit: limit || 4,
  };
};

export const getAllPieChartApiConfigs = (
  category: KpiCategory | undefined,
): PieChartApiConfig[] => {
  if (!category) return [];

  const pieMetrics = getMetricsByDisplayType(category, "pie");
  return pieMetrics
    .map((metric) => getPieChartApiConfig(metric))
    .filter((config): config is PieChartApiConfig => config !== null);
};

export interface TreeChartConfig {
  metricId: string;
  title: string;
  dimensionIds: string[];
  sortBy: { metricId: string; desc: boolean };
  limit: number;
}

export const getTreeChartConfig = (
  category: KpiCategory | undefined,
): TreeChartConfig | null => {
  if (!category) return null;

  const treeMetrics = getMetricsByDisplayType(category, "tree");
  if (treeMetrics.length === 0) return null;

  const metric = treeMetrics[0];
  const { metricIds, dimensionIds, sortBy, limit } = metric.api;

  return {
    metricId: metricIds[0],
    title: metric.title,
    dimensionIds: dimensionIds || ["country", "region", "city"],
    sortBy: sortBy?.[0]
      ? { metricId: sortBy[0].metricId, desc: sortBy[0].desc }
      : { metricId: metricIds[0], desc: true },
    limit: limit || 4,
  };
};

export const getNextDimensionFromArray = (
  currentDimension: string,
  dimensionIds: string[],
): string | null => {
  const currentIndex = dimensionIds.indexOf(currentDimension);
  if (currentIndex === -1 || currentIndex >= dimensionIds.length - 1) {
    return null;
  }
  return dimensionIds[currentIndex + 1];
};

export const getMetricGoal = (
  category: KpiCategory | undefined,
  metricId: string,
): number | undefined => {
  if (!category) return undefined;
  const metric = category.metrics.find((m) => m.id === metricId);
  return metric?.goal;
};

export const calculateYAxisDomain = (
  dataPoints: Array<{ current?: number }>,
  goal?: number,
): [number, number] | undefined => {
  if (!dataPoints || dataPoints.length === 0) return undefined;

  const dataValues = dataPoints
    .map((d) => d.current)
    .filter(Boolean) as number[];
  if (dataValues.length === 0) return undefined;

  const dataMax = Math.max(...dataValues);
  const dataMin = Math.min(...dataValues);

  if (goal !== undefined && goal > dataMax) {
    // If goal is higher than data, extend Y-axis to include it
    return [Math.min(0, dataMin), goal * 1.1];
  }

  return undefined;
};
